'use client';
import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

const DEBOUNCE_WAIT_TIME = 500;

export type QuantitySelectorProps = {
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  disabled: boolean;
  className?: string;
  withDebounce?: boolean;
  minVal?: number;
  allowZero?: boolean;
};

function isPossibleToDecrease(quantity: number | '', minVal: number): quantity is number {
  return quantity !== '' && quantity > 0 && quantity > minVal;
}

function isPossibleToSetToZero(quantity: number | '', minVal: number, allowZero = false): quantity is number {
  return !!(quantity === 0 && minVal && allowZero);
}

function QuantitySelector({
  quantity,
  onQuantityChange,
  className,
  disabled,
  allowZero = false,
  minVal = 0,
  withDebounce = true,
}: QuantitySelectorProps) {
  const [internalQuantity, setInternalQuantity] = useState<number | ''>(quantity);
  const buttonStyle =
    'button not-disabled:bg-white w-12 h-full hover:not-disabled:bg-gray-100! focus:outline-none not-disabled:text-black-900 font-bold text-base flex-grow-0';

  const isDecreaseDisabled = disabled || quantity <= minVal || internalQuantity === '' || internalQuantity <= minVal;

  function onDecreaseClick() {
    if (isPossibleToDecrease(internalQuantity, minVal)) {
      const newValue = internalQuantity - 1;
      setInternalQuantity(newValue);
      debounceOnQuantityChange(newValue);
    }
  }

  function onIncreaseClick() {
    const newValue = (internalQuantity || 0) + 1;
    setInternalQuantity(newValue);
    debounceOnQuantityChange(newValue);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnQuantityChange = useCallback(
    debounce(
      (quantity: number) => {
        onQuantityChange(quantity);
      },
      withDebounce ? DEBOUNCE_WAIT_TIME : 0,
    ),
    [onQuantityChange],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnQuantityInputChange = useCallback(
    debounce(
      (quantity: number | '') => {
        if (quantity === '' || isNaN(quantity) || internalQuantity === quantity || disabled) {
          return;
        }

        if (isPossibleToSetToZero(quantity, minVal, allowZero)) {
          onQuantityChange(quantity);
          return;
        }

        if (quantity < minVal) {
          setInternalQuantity(minVal);
        }
        onQuantityChange(quantity);
      },
      withDebounce ? DEBOUNCE_WAIT_TIME : 0,
    ),
    [onQuantityChange],
  );

  function onInputChanged(e: React.ChangeEvent<HTMLInputElement>) {
    const newValueAsString = e.target.value;
    const newValue = parseInt(newValueAsString, 10);

    if (newValueAsString === '') {
      setInternalQuantity(newValueAsString);
      debounceOnQuantityInputChange(newValueAsString);
    }

    if (isNaN(newValue) || internalQuantity === newValue) {
      return;
    }

    setInternalQuantity(newValue);
    debounceOnQuantityInputChange(newValue);
  }

  function onInputBlur(e: React.FocusEvent<HTMLInputElement>) {
    const valueAsString = e.target.value;
    if (valueAsString === '') {
      setInternalQuantity(1);
      debounceOnQuantityInputChange(1);
    }
  }

  function onFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
  }

  useEffect(() => {
    setInternalQuantity(quantity);
  }, [quantity]);

  return (
    <form
      className={classNames(
        'inline-flex flex-row justify-center items-center bg-white border border-solid border-gray-300 h-[50px]',
        className,
      )}
      data-testid="quantity-selector"
      onSubmit={onFormSubmit}
    >
      <button
        className={`${buttonStyle} -mr-[1px]`}
        data-testid="decrease-quantity-button"
        type="button"
        onClick={() => onDecreaseClick()}
        disabled={isDecreaseDisabled}
      >
        <span className="m-auto">−</span>
      </button>
      <input
        type="text"
        data-testid="change-quantity-input"
        inputMode="numeric"
        min={minVal}
        className="outline-none relative focus:outline-none text-center flex-1 w-12 h-full text-base hover:text-black focus:text-black border-y-0 focus:border-gray-300 no-controls px-1"
        value={internalQuantity}
        disabled={disabled}
        onChange={onInputChanged}
        onBlur={onInputBlur}
      />
      <button
        className={`${buttonStyle} -ml-[2px]`}
        data-testid="increase-quantity-button"
        type="button"
        onClick={() => onIncreaseClick()}
        disabled={disabled}
      >
        <span className="m-auto">+</span>
      </button>
    </form>
  );
}

export default QuantitySelector;
